import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import TabItem from "./TabItem";
import { TabPanelProps } from "./UserProfileTabs";
import UserManagementTable from "./UserManagementTable";
import { AdminApi } from "../../../services/adminApi";
import {
  KeycloakUserRepresentation,
  KeycloakGroupRepresentation,
  UserWithGroupsAndRoles,
} from "../../../global/types/user";

// Available application roles
const AVAILABLE_ROLES = ["viewer", "specialist", "admin"];

export default function UserManagementTab(props: TabPanelProps) {
  const { value, index } = props;

  const [users, setUsers] = useState<UserWithGroupsAndRoles[]>([]);
  const [allGroups, setAllGroups] = useState<KeycloakGroupRepresentation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch users and groups
  // The backend now returns users with their groups and roles already populated
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch users (already includes groups and roles) and groups in parallel
      const [usersData, groupsData] = await Promise.all([
        AdminApi.getUsers(),
        AdminApi.getGroups(),
      ]);

      // Map users to the expected format
      const usersWithData: UserWithGroupsAndRoles[] = usersData.map((user) => ({
        id: user.id,
        username: user.username,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        enabled: user.enabled,
        emailVerified: user.emailVerified,
        createdTimestamp: user.createdTimestamp,
        roles: user.roles || [],
        groups: user.groups.map(g => ({
          id: g.id,
          name: g.name,
          path: g.path,
        })),
      }));

      // Map groups to the expected format
      const groupsList: KeycloakGroupRepresentation[] = groupsData.map(g => ({
        id: g.id,
        name: g.name,
        path: g.path,
        attributes: { allowed_layers: g.allowedLayers },
      }));

      setUsers(usersWithData);
      setAllGroups(groupsList);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err.message || "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Load data when tab is active
  useEffect(() => {
    if (value === index) {
      fetchData();
    }
  }, [value, index]);

  // Handle delete user
  const handleDeleteUser = async (userId: string) => {
    try {
      await AdminApi.deleteUser(userId);

      // Refresh users list
      await fetchData();
    } catch (err: any) {
      console.error("Error deleting user:", err);
      setError(err.message || "Erro ao excluir usuário");
    }
  };

  // Handle group change for a user (legacy, keeping for compatibility)
  const handleGroupChange = async (
    userId: string,
    newGroupId: string | null,
    oldGroupId: string | null
  ) => {
    try {
      await AdminApi.changeUserGroup(userId, { newGroupId, oldGroupId });

      // Refresh users list to show updated groups
      await fetchData();
    } catch (err: any) {
      console.error("Error changing user group:", err);
      setError(err.message || "Erro ao alterar grupo do usuário");
    }
  };

  // Handle roles change for a user
  const handleRolesChange = async (userId: string, newRole: string) => {
    try {
      // Convert single role to array for API
      const newRoles = newRole ? [newRole] : [];
      await AdminApi.changeUserRoles(userId, newRoles);

      // Update local state immediately for better UX
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, roles: newRoles } : user
        )
      );
    } catch (err: any) {
      console.error("Error changing user roles:", err);
      setError(err.message || "Erro ao alterar roles do usuário");
      // Refresh to get correct state
      await fetchData();
    }
  };

  const userManagementItem = {
    title: "Usuários",
    description: "Visualize e gerencie usuários cadastrados na plataforma",
    content: (
      <UserManagementTable
        users={users}
        allGroups={allGroups}
        availableRoles={AVAILABLE_ROLES}
        loading={loading}
        error={error}
        onDeleteUser={handleDeleteUser}
        onGroupChange={handleGroupChange}
        onRolesChange={handleRolesChange}
        onRefresh={fetchData}
      />
    ),
  };

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <TabItem {...userManagementItem} />
        </Box>
      )}
    </div>
  );
}
